<template>
  <!-- Error Message -->
  <PageError v-if="error"
   :error="error"
   :errorCode="errorCode"
  />

  <div v-else-if="canViewOrg || user?.is_staff" class="p-6 dark:text-white">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Organisations<template v-if="activeTab === 'orgs'"> - {{ filteredOrgs.length }}</template>
        <span v-if="isFiltered && activeTab === 'orgs'" class="text-sm text-gray-500">(filtered)</span>
      </h1>

      <div class="flex gap-4">
        <div class="relative group">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          title="Organisation UI Tutorial"
          @click="startTutorial"
        >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
        <button v-if="activeTab === 'orgs' && (canAddOrg || user?.is_staff)"
          id="add-organisation-btn"
          @click="
            $router.push({ name: 'OrganizationDetails', params: { id: 'new' } })
          "
          class="btn bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add Organisation
        </button>
      </div>
      
    </div>

    <!-- Tab Navigation (mirrors OrganizationTabs.vue styling) -->
    <div class="mb-6">
      <div class="backdrop-blur-sm py-3 px-3 glass-card-lite">
        <nav class="-mb-px flex space-x-8 overflow-x-auto">
          <button
            v-for="tab in visibleTabs"
            :key="tab.key"
            @click="setTab(tab.key)"
            :class="[
              'px-4 py-2 font-bold transition-all duration-200 flex items-center space-x-2 rounded-md border border-white glass-card',
              activeTab === tab.key
                ? 'bg-transparent text-fuchsia-500'
                : 'text-gray-400 hover:bg-slate-800/30 hover:text-fuchsia-500 bg-transparent',
            ]"
          >
            {{ tab.label }}
          </button>
        </nav>
      </div>
    </div>

    <!-- Organisations Tab Content -->
    <template v-if="activeTab === 'orgs'">

    <!-- Search and Filter Section -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
    >
      <!-- Search Bar -->
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search organisations by name..."
        />
      </div>

      <!-- Bulk Action Section -->
      <div class="flex items-center mt-4 md:mt-0 space-x-2">
        <GlassSelect
          v-model="bulkAction"
          :options="canDeleteOrg || user?.is_staff ? [{ value: 'delete', label: 'Delete selected items' }] : []"
          placeholder="Bulk Actions"
          :valueKey="'value'"
          :labelKey="'label'"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedOrgs.length === 0"
          :class="[
            'btn focus:outline-none focus:ring-2 focus:ring-blue-500',
            bulkAction && selectedOrgs.length
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>


    <GenericTable
      id="organisations-table"
      ref="table"
      :columns="columns"
      :items="paginatedOrgs"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedOrgs"
      @rowClick="goToOrgDetail"
    >
      <!-- Custom cell templates -->
      <template #is_service_provider="{ value }">
        {{ value ? "Yes" : "No" }}
      </template>
      <template #created_date="{ value }">
        {{ formatDateTime(value) }}
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
          :valueKey="'value'"
          :labelKey="'label'"
          placeholder="Items per page"
          @change="updatePagination"
        />
      </div>
    </div>

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="componetObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem=false"
    />
    </template>

    <!-- SNMP Profiles Tab Content -->
    <SNMPProfileList v-if="activeTab === 'snmp'" :embedded="true" />

    <!-- Fortigate Profiles Tab Content -->
    <FortigateProfileList v-if="activeTab === 'fortigate'" :embedded="true" />

    <!-- WiFi Profiles Tab Content -->
    <WiFiProfileListView v-if="activeTab === 'wifi'" :embedded="true" />

  </div>
</template>

<script>
import { getAllOrgs, deleteOrg } from "@/services/orgService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import PageError from "@/components/PageError.vue";
import { useStore } from "@/store";
import { getAllSubnets } from "@/services/subnetService";
import { getAllVlans } from "@/services/vlanService";
import { getAllBlackboxes } from "@/services/blackboxService";
import { getAllProbes } from "@/services/probeService";
import { getAllDivisions } from "@/services/divisionService";
import { getAllSnmps } from "@/services/snmpService";
import { getAllHosts } from "@/services/hostService";
import { getSites } from "@/services/siteService";
import { getAllModules } from "@/services/moduleService";
import { getAllTunnels } from "@/services/tunnelService";
import { getAllLoadsheddings } from "@/services/loadshedding-service";
import DeleteModal from "@/components/DeleteModal.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import GlassSelect from "@/components/GlassSelect.vue";
import { useTutorial } from "@/composables/tutorial";
import SNMPProfileList from "@/views/SNMPProfileList.vue";
import FortigateProfileList from "@/views/FortigateProfileList.vue";
import WiFiProfileListView from "@/views/WiFiProfileListView.vue";

const TAB_KEYS = ["orgs", "snmp", "fortigate", "wifi"];

export default {
  name: "OrganizationView",
  components: {
    SearchBar,
    GenericTable,
    PageError,
    DeleteModal,
    GlassSelect,
    SNMPProfileList,
    FortigateProfileList,
    WiFiProfileListView,
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      orgs: [],
      searchQuery: "",
      filteredOrgs: [],
      currentPage: 1,
      itemsPerPage: 10,
      selectedOrgs: [],
      deletingItem: false,
      bulkAction: "",
      vlans:[],
      subnets:[],
      blackboxes:[],
      tunnels:[],
      loadshedding:[],
      probes:[],
      sites:[],
      hosts:[],
      divisions:[],
      uiconfigs:[],
      modules:[],
      snmps:[],
      columns: [
        { key: "name", label: "Name", sortable: true, width: "25%" },
        {
          key: "abbreviated_name",
          label: "Abbreviated Name",
          sortable: true,
          width: "20%",
        },
        {
          key: "is_service_provider",
          label: "Service Provider",
          sortable: true,
          width: "15%",
        },
        { key: "code", label: "Code", sortable: true, width: "15%" },
        {
          key: "created_date",
          label: "Created Date",
          sortable: true,
          width: "25%",
        },
      ],
      canViewOrg: false,
      canAddOrg: false,
      canDeleteOrg: false,
      canViewSNMPProfile: false,
      canViewFortigateProfile: false,
      canViewWiFiProfile: false,
      activeTab: "orgs",
      error: "",
      errorMessages: {
        400 : "Bad Request .The server could not understand the request.",
        401 : "Unauthorized access. Please check your authentication token or log in again.",
        403 : "You do not have permission to access this resource. Contact a staff member to assign you the permissions to access this page.",
        404 : "Page Not Found.",
        500 : "Internal Server Error - Something went wrong on the server."
      },
      isDeleting: false,
      orgTutorialSteps: [
        {
          element: '#searchbar',
          action: 'display',
          message: 'Search organisations by name or abbreviated name.'
        },
        {
          element: '#add-organisation-btn',
          action: 'display',
          message: 'Click here to create a new organisation.'
        },
        {
          element: '#organisations-table',
          action: 'display',
          message: 'Click any row to view or edit organisation details.'
        },
      ],
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    }, 
    isFiltered() {
      return this.searchQuery !== "";
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredOrgs.length / this.itemsPerPage);
    },
    paginatedOrgs() {
      if (this.itemsPerPage === -1) {
        return this.filteredOrgs;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredOrgs.slice(start, end);
    },
    componetObjects() {
      return this.selectedOrgs.map(org => {

        // Filter probes and sites related to the organisation

        const sites  = this.sites.filter(site => site.org === org.id);
        const probes = this.probes.filter(probe => sites.some(item => item.id  === probe.site));
        
        return {
          values: [
            { name: "Organisation", value: org.name },
            { name: "Sites", value: org.related_objects.sites },
            { name: "Tunnels", value: org.related_objects.tunnels },
            { name: "Loadshedding Areas", value: org.related_objects.loadshedding_areas },
            { name: "Probes", value: org.related_objects.probes },
            { name: "Subnets", value: org.related_objects.subnets },
            { name: "VLANs", value: org.related_objects.vlans },
            { name: "Endpoint Availabilities", value: org.related_objects.endpoint_availabilities },
            { name: "SNMPs", value: org.related_objects.snmps },
            { name: "Important hosts", value: org.related_objects.important_hosts },
            { name: "Divisions", value: org.related_objects.divisions },
            { name: "Fortigates", value: org.related_objects.fortigates}
            // { name: "UIconfigs", value: org.related_objects.uiconfigs },
            // { name: "Modules", value: org.related_objects.modules },
          ]
        }
      })
    },
    relatedOrganisations(){
      const splitpoint = this.orgs.find(item => item.name === "Splitpoint");
      const relatedOrgs = this.orgs.filter(item => item.service_provider === splitpoint.id);
      return relatedOrgs;
    },
    visibleTabs() {
      const isStaff = this.user?.is_staff;
      const tabs = [];
      if (this.canViewOrg || isStaff) {
        tabs.push({ key: "orgs", label: "Organisations" });
      }
      if (this.canViewSNMPProfile || isStaff) {
        tabs.push({ key: "snmp", label: "SNMP Profiles" });
      }
      if (this.canViewFortigateProfile || isStaff) {
        tabs.push({ key: "fortigate", label: "Fortigate Profiles" });
      }
      if (this.canViewWiFiProfile || isStaff) {
        tabs.push({ key: "wifi", label: "WiFi Profiles" });
      }
      return tabs;
    },
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true
    },
    searchQuery() {
      this.applyFilters();
    },
    "$route.query.tab"(newTab) {
      this.syncActiveTabFromRoute(newTab);
    },
  },
  mounted() {
    this.fetchOrganizations();
    this.checkPermissions();
    this.syncActiveTabFromRoute(this.$route.query.tab);
  },
  methods: {
    async startTutorial() {
      await this._start(this.orgTutorialSteps);
    },
    async checkPermissions() {
      if (this.user) {
        this.canAddOrg = await useStore.hasPermission("Can add organisation");
        this.canDeleteOrg = await useStore.hasPermission("Can delete organisation");
        this.canViewOrg = await useStore.hasPermission("Can view organisation");
        this.canViewSNMPProfile = await useStore.hasPermission("Can view SNMP Metric Profile");
        this.canViewFortigateProfile = await useStore.hasPermission("Can view Fortigate Exporter Profile");
        this.canViewWiFiProfile = await useStore.hasPermission("Can view WiFi Profile");
        this.syncActiveTabFromRoute(this.$route.query.tab);
      }
    },
    syncActiveTabFromRoute(routeTab) {
      const isStaff = this.user?.is_staff;
      const tabPermMap = {
        orgs: this.canViewOrg || isStaff,
        snmp: this.canViewSNMPProfile || isStaff,
        fortigate: this.canViewFortigateProfile || isStaff,
        wifi: this.canViewWiFiProfile || isStaff,
      };
      if (TAB_KEYS.includes(routeTab) && tabPermMap[routeTab]) {
        this.activeTab = routeTab;
      } else {
        this.activeTab = "orgs";
      }
    },
    setTab(key) {
      if (this.activeTab === key) return;
      this.activeTab = key;
      const query = { ...this.$route.query };
      if (key === "orgs") {
        delete query.tab;
      } else {
        query.tab = key;
      }
      this.$router.replace({ query });
    },
    async fetchOrganizations() {
      try {
        this.orgs = await getAllOrgs();
        this.applyFilters();
      } catch (error) {
        console.error("Error fetching organizations:", error);
        if (error.response) {
          this.error = `${this.errorMessages[error.response.status]}`
          this.errorCode = `${error.response.status}`
        } else{
          this.error = `Unknown Error`
        }
      }
    },
    goToOrgDetail(org) {
      this.$router.push({
        name: "OrganizationDetails",
        params: { id: org.id },
      });
    },
    updateSelectedOrgs(selectedItems) {
      this.selectedOrgs = selectedItems;
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete" ) {
        this.deletingItem = true;
      }
      this.bulkAction = "";
    },
    async confirmDelete() {
      this.isDeleting = true;
      await this.deleteSelectedOrgs();
      await this.tenant.fetchOrganizations();
      this.deletingItem = false; 
      this.bulkAction = "";
    },
    async deleteSelectedOrgs() {
      if (this.selectedOrgs.length === 0) {
        alert("No organizations selected for deletion");
        return;
      }

      try {
        const deletePromises = this.selectedOrgs.map((org) =>
          deleteOrg(org.id)
        );
        await Promise.all(deletePromises);

        this.orgs = this.orgs.filter(
          (org) => !this.selectedOrgs.some((item) => item.id === org.id)
        );
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedOrgs = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert(
          "An error occurred while deleting organizations. Please try again."
        );
      }  finally {
        this.isDeleting = false;
      }
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    async deleteOrgItem(id) {
      if (confirm("Are you sure you want to delete this organization?")) {
        try {
          await deleteOrg(id);
          this.orgs = this.orgs.filter((org) => org.id !== id);
          this.applyFilters(); // Re-apply filters to update the view
        } catch (error) {
          console.error("Error deleting organization:", error);
          // Handle error (e.g., show error message to user)
        }
      }
    },
    applyFilters() {
      this.filteredOrgs = this.orgs.filter(
        (org) =>
          org.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          org.abbreviated_name
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase())
      );
      this.currentPage = 1;
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
  },
};
</script>
